// The cache of the level picker of the build card (mockup round 084 card
// 17, R1839; ui-map-interface.md §8 "The build card").
//
// An answer to a PreviewQuery holds for one building while the Empire
// frame's row of that building stays the same: the level, the queued
// upgrades and the figures it states. A change of the row (a level lands,
// a job is queued, a node moves a rate) makes the card ask again. The
// module imports no state, so `state.ts` can hold the cache.

import type { BuildingLevel, BuildingPreview } from '../../gen/proto/aquaphobia';

/** What an answer depends on: the Empire frame's row of the building. */
export function rowSig(bl: BuildingLevel | undefined): string {
  if (!bl) return '';
  return `${bl.level}|${bl.queued}|` + bl.effects.map((e) => `${e.kind}:${e.now}:${e.next}`).join(',');
}

/** The answers of the level picker, one per building, and the queries
 *  that wait for one. */
export class LevelPreviews {
  private readonly got = new Map<number, { sig: string; p: BuildingPreview }>();
  private readonly asked = new Map<number, string>();

  /** The answer that still holds for this row, or null. */
  get(bl: BuildingLevel | undefined): BuildingPreview | null {
    if (!bl) return null;
    const hit = this.got.get(bl.building);
    return hit && hit.sig === rowSig(bl) ? hit.p : null;
  }

  /**
   * Whether the card must ask for this row: no answer holds and no query
   * for the same row waits. A true answer marks the query as sent.
   */
  shouldAsk(bl: BuildingLevel | undefined): boolean {
    if (!bl || this.get(bl)) return false;
    const sig = rowSig(bl);
    if (this.asked.get(bl.building) === sig) return false;
    this.asked.set(bl.building, sig);
    return true;
  }

  /**
   * One answer. It is kept under the row it was asked for, when the level
   * and the queue it states are that row's; an answer to an older row is
   * dropped, and the card asks again for the new one.
   */
  take(p: BuildingPreview): void {
    const sig = this.asked.get(p.building);
    if (sig === undefined) return;
    this.asked.delete(p.building);
    if (!sig.startsWith(`${p.level}|${p.queued}|`)) return;
    this.got.set(p.building, { sig, p });
  }

  /** A new hello: a query the old socket sent will get no answer. */
  reset(): void {
    this.got.clear();
    this.asked.clear();
  }
}
